import type { WalletName } from "@solana/wallet-adapter-base";
import { WalletAdapterNetwork } from "@solana/wallet-adapter-base";
import {
  ConnectionProvider,
  WalletProvider,
} from "@solana/wallet-adapter-react";
import {
  SolflareWalletAdapter,
  Coin98WalletAdapter,
  MathWalletAdapter,
  BitKeepWalletAdapter,
  WalletConnectWalletAdapter,
  CoinbaseWalletAdapter,
  XDEFIWalletAdapter,
  SafePalWalletAdapter,
  CloverWalletAdapter,
  PhantomWalletAdapter,
} from "@solana/wallet-adapter-wallets";
import {
  SolanaMobileWalletAdapter,
  createDefaultAddressSelector,
  createDefaultAuthorizationResultCache,
  createDefaultWalletNotFoundHandler,
} from "@solana-mobile/wallet-adapter-mobile";
import type { FC, ReactNode } from "react";
import { useMemo } from "react";

import { DEFAULT_ENDPOINT } from "../utils/web3/endpoints";
import Images from "styles/image-library";
import HardcodedWalletStandardAdapter from "types/hardcoded-wallet-adapter";

export const WalletConnectionProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const wallets = useMemo(() => {
    const knownWallets = [
      new SolanaMobileWalletAdapter({
        addressSelector: createDefaultAddressSelector(),
        appIdentity: {
          name: "Marinade.finance",
          uri: "https://marinade.finance",
          icon: Images.favicon,
        },
        authorizationResultCache: createDefaultAuthorizationResultCache(),
        cluster:
          DEFAULT_ENDPOINT.name === "mainnet-beta"
            ? WalletAdapterNetwork.Mainnet
            : WalletAdapterNetwork.Devnet,
        onWalletNotFound: createDefaultWalletNotFoundHandler(),
      }),
      new PhantomWalletAdapter(),
      new SolflareWalletAdapter(),
      new CoinbaseWalletAdapter(),
      new WalletConnectWalletAdapter({
        network:
          DEFAULT_ENDPOINT.name === "mainnet-beta"
            ? WalletAdapterNetwork.Mainnet
            : WalletAdapterNetwork.Devnet,
        options: {
          projectId: "db35025fb8b24720c2ad876d976080e5",
          metadata: {
            name: "Marinade Finance",
            description: "Marinade Finance",
            url: "https://marinade.finance/",
            icons: ["https://marinade.finance/marinade-icon-black.svg"],
          },
        },
      }),
      new BitKeepWalletAdapter(),
      new CloverWalletAdapter(),
      new SafePalWalletAdapter(),
      new XDEFIWalletAdapter(),
      new MathWalletAdapter(),
      new Coin98WalletAdapter(),
    ];

    // eslint-disable-next-line @typescript-eslint/dot-notation, @typescript-eslint/no-explicit-any
    if (typeof (window as any)["okxwallet"] === "undefined")
      return [
        ...knownWallets.slice(0, 2),
        new HardcodedWalletStandardAdapter({
          name: "OKX Wallet" as WalletName,
          url: "https://www.okx.com/web3",
          icon: "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADAAAAAwCAYAAABXAvmHAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAJDSURBVHgB7Zq9jtpAEMfHlhEgQLiioXEkoAGECwoKxMcTRHmC5E3IoyRPkPAEkI7unJYmTgEFTYwA8a3NTKScLnCHN6c9r1e3P2llWQy7M/s1Gv1twCP0ej37dDq9x+Zut1t3t9vZjDEHIiSRSPg4ZpDL5fxkMvn1cDh8m0wmfugfO53OoFQq/crn8wxfY9EymQyrVCqMfHvScZx1p9ls3pFxXBy/bKlUipGPrVbLuQqAfsCliq3zl0H84zwtjQrOw4Mt1W63P5LvBm2d+Xz+YzqdgkqUy+WgWCy+Mc/nc282m4FqLBYL+3g8fjDxenq72WxANZbLJeA13zDX67UDioL5ybXwafMYu64Ltn3bdDweQ5R97fd7GyhBQMipx4POeEDHIu2LfDdBIGGz+hJ9CQ1ABjoA2egAZPM6AgiCAEQhsi/C4jHyPA/6/f5NG3Ks2+3CYDC4aTccDrn6ojG54MnEvG00GoVmWLIRNZ7wTCwDHYBsdACy0QHIhiuRETxlICWpMMhGZHmqS8qH6JLyGegAZKMDkI0uKf8X4SWlaZo+Pp1bRrwlJU8ZKLIvUjKh0WiQ3sRUbNVq9c5Ebew7KEo2m/1p4jJ4qAmDaqDQBzj5XyiAT4VCQezJigAU+IDU+z8vJFnGWeC+bKQV/5VZ71FV6L7PA3gg3tXrdQ+DgLhC+75Wq3no69P3MC0NFQpx2lL04Ql9gHK1bRDjsSBIvScBnDTk1WrlGIZBorIDEYJj+rhdgnQ67VmWRe0zlplXl81vcyEt0rSoYDUAAAAASUVORK5CYII=",
        }),
        ...knownWallets.slice(2),
      ];

    return knownWallets;
  }, []);

  return (
    <ConnectionProvider
      endpoint={DEFAULT_ENDPOINT.endpoint}
      config={{
        wsEndpoint: DEFAULT_ENDPOINT.wsEndpoint,
        commitment: "confirmed",
      }}
    >
      <WalletProvider wallets={wallets} autoConnect>
        {children}
      </WalletProvider>
    </ConnectionProvider>
  );
};
